import React from "react";
import { motion } from "framer-motion";
import {
  PhoneCall,
  ClipboardList,
  Home,
  Sparkles,
  HeartHandshake,
  Gift,
  Check,
} from "lucide-react";
import { IMAGES } from "@/lib/site";

const phases = [
  {
    id: "phase-1",
    label: "Step 1",
    title: "Connect With Our Care Team",
    icon: PhoneCall,
    description:
      "Share your delivery details, recovery needs, and preferred stay duration.",
    chips: ["Free guidance", "Quick response"],
  },
  {
    id: "phase-2",
    label: "Step 2",
    title: "Choose Your Recovery Package",
    icon: ClipboardList,
    description:
      "Select a package designed around your recovery journey.",
    chips: ["Transparent pricing", "No hidden charges"],
  },
  {
    id: "phase-3",
    label: "Step 3",
    title: "Arrive & Feel at Home",
    icon: Home,
    description:
      "Settle into your private villa and comfortable recovery environment.",
    chips: ["Private stay", "Calm surroundings"],
  },
  {
    id: "phase-4",
    label: "Step 4",
    title: "Daily Ayurvedic Care Begins",
    icon: Sparkles,
    description:
      "Personalized therapies, nourishing meals, and guided care.",
    chips: ["Doctor-guided", "Daily support"],
  },
  {
    id: "phase-5",
    label: "Step 5",
    title: "Recover With Confidence",
    icon: HeartHandshake,
    description:
      "Focus on rest and healing while your body regains strength.",
    chips: ["Physical recovery", "Emotional well-being"],
  },
  {
    id: "phase-6",
    label: "Step 6",
    title: "Continue Your Journey at Home",
    icon: Gift,
    description:
      "Leave with guidance for diet, routines, and continued wellness.",
    chips: ["Recovery guidance", "Continued support"],
  },
];

export const CareJourney = () => {
  return (
    <section
      id="journey"
      className="relative py-16 md:py-36 bg-sand/40 bg-grain"
      data-testid="care-journey-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
            The Care Journey
          </div>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
            From enquiry to recovery, <span className="italic text-sage-700">know exactly what your stay looks like</span>.
          </h2>
          <p className="mt-5 text-base md:text-lg text-charcoal/65 leading-relaxed">
            Six gentle steps from your first message to your continued wellness
            at home, designed so nothing feels uncertain.
          </p>
        </motion.div>

        <div className="mt-10 lg:mt-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 relative">
            {/* vertical connecting line */}
            <div className="absolute left-[27px] md:left-[31px] top-2 bottom-2 w-px bg-sage/25" aria-hidden />

            <ol className="space-y-7 md:space-y-12 lg:space-y-14">
              {phases.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.li
                    key={p.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{
                      duration: 0.7,
                      delay: i * 0.08,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="relative pl-20 md:pl-24"
                    data-testid={`care-${p.id}`}
                  >
                    <div className="absolute left-0 top-0 w-14 h-14 md:w-[62px] md:h-[62px] rounded-full bg-ivory border border-sage/30 flex items-center justify-center text-sage">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
                      {p.label}
                    </div>
                    <h3 className="mt-2 font-serif text-2xl md:text-3xl text-charcoal">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-base text-charcoal/70 leading-relaxed max-w-xl">
                      {p.description}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.chips.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border border-sage/25 text-charcoal/75 bg-ivory/70"
                        >
                          <Check size={12} strokeWidth={2} className="text-sage shrink-0" />
                          {c}
                        </span>
                      ))}
                    </div>
                  </motion.li>
                );
              })}
            </ol>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block lg:col-span-5"
          >
            <div className="lg:sticky lg:top-28">
              <div className="relative aspect-[4/5] w-full rounded-3xl overflow-hidden bg-ivory">
                <img
                  src={IMAGES.ayurvedaKit}
                  alt="A mother holding her newborn, an illustration of the unhurried Ayurvedic postnatal care that defines Kakkanat, Kerala"
                  className="w-full h-full object-cover"
                  data-testid="care-journey-image"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CareJourney;
